import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import type {
  List,
  ListItem,
  PhrasingContent,
  RootContent,
  Table,
} from "mdast";
import "mdast-util-math";

const REFERENCE_TOKEN_REGEX = /[ \t]*\[ID:[^\]]+\]/g;

/** Serialize inline (phrasing) nodes to a flat string, dropping format markers. */
const inlineToText = (node: PhrasingContent): string => {
  switch (node.type) {
    case "text":
      return node.value;
    case "inlineCode":
      return node.value;
    case "strong":
    case "emphasis":
    case "delete":
      return node.children.map(inlineToText).join("");
    case "link": {
      const text = node.children.map(inlineToText).join("");
      if (!node.url) return text;
      if (text === node.url || text.length === 0) return node.url;
      return `${text} (${node.url})`;
    }
    case "image":
      return node.alt ?? "";
    case "break":
      return "\n";
    case "inlineMath":
      return "$" + node.value + "$";
    default:
      return "";
  }
};

const phrasingChildren = (
  children: ReadonlyArray<PhrasingContent>,
): string => children.map(inlineToText).join("");

const serializeTable = (table: Table): string => {
  const rows = table.children.map((row) =>
    row.children.map((cell) => phrasingChildren(cell.children)),
  );
  if (rows.length === 0) return "";
  const columnCount = Math.max(...rows.map((r) => r.length));
  const pad = (cells: string[]): string[] => {
    const next = cells.slice();
    while (next.length < columnCount) next.push("");
    return next;
  };
  const formatRow = (cells: string[]): string => `| ${pad(cells).join(" | ")} |`;
  const separator = formatRow(new Array(columnCount).fill("---"));
  const [header, ...body] = rows;
  return [formatRow(header), separator, ...body.map(formatRow)].join("\n");
};

const serializeList = (list: List, depth: number): string => {
  const indent = "\t".repeat(depth);
  const lines: string[] = [];
  let index = list.start ?? 1;
  for (const item of list.children as ListItem[]) {
    const marker = list.ordered ? `${index}.` : "-";
    index += 1;
    const textParts: string[] = [];
    const nestedLists: List[] = [];
    for (const child of item.children) {
      if (child.type === "list") nestedLists.push(child);
      else textParts.push(serializeBlock(child, depth));
    }
    lines.push(`${indent}${marker} ${textParts.join(" ").trim()}`);
    for (const nested of nestedLists) {
      lines.push(serializeList(nested, depth + 1));
    }
  }
  return lines.join("\n");
};

const serializeBlock = (node: RootContent, depth: number): string => {
  switch (node.type) {
    case "paragraph":
    case "heading":
      return phrasingChildren(node.children);
    case "code":
      return node.value;
    case "blockquote":
      return node.children
        .map((child) => serializeBlock(child, depth))
        .filter((s) => s.length > 0)
        .join("\n\n");
    case "list":
      return serializeList(node, depth);
    case "table":
      return serializeTable(node);
    case "thematicBreak":
      return "";
    case "math":
      return "$$\n" + node.value + "\n$$";
    default:
      return "";
  }
};

const stripReferences = (text: string): string =>
  text
    .replace(REFERENCE_TOKEN_REGEX, "")
    .replace(/[ \t]+([.,;:!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ");

export function markdownToPlainText(content: string): string {
  const tree = unified().use(remarkParse).use(remarkGfm).use(remarkMath).parse(content);
  const text = tree.children
    .map((node) => serializeBlock(node, 0))
    .filter((s) => s.length > 0)
    .join("\n\n");
  return stripReferences(text).trim();
}
